// react
import React from 'react';

// tanstack
import { Link } from '@tanstack/react-router';

// material ui
import {
  alpha,
  ClickAwayListener,
  Fade,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';

// relative
import type { NavItem } from './navigationTypes';

interface NavigationPopperProps {
  anchorEl: HTMLElement | null;
  items: NavItem[];
  onClose: () => void;
}

export const NavigationPopper: React.FC<NavigationPopperProps> = ({
  anchorEl,
  items,
  onClose,
}) => {
  return (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="right-start"
      transition
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={200}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              ml: 2,
              minWidth: 200,
              overflow: 'hidden',
              boxShadow: 12,
            }}
          >
            <ClickAwayListener onClickAway={onClose}>
              <MenuList
                dense
                sx={{
                  px: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                {items.map((child) => {
                  if (!child.path) {
                    return (
                      <MenuItem
                        key={child.id}
                        onClick={onClose}
                        sx={{
                          py: 1,
                          px: 2,
                          borderRadius: 2,
                        }}
                      >
                        {child.label}
                      </MenuItem>
                    );
                  }

                  return (
                    <Link
                      key={child.id}
                      to={child.path}
                      activeProps={{
                        style: {
                          textDecoration: 'none',
                          color: 'inherit',
                          width: '100%',
                          display: 'block',
                        },
                      }}
                      inactiveProps={{
                        style: {
                          textDecoration: 'none',
                          color: 'inherit',
                          width: '100%',
                          display: 'block',
                        },
                      }}
                    >
                      {({ isActive }) => (
                        <MenuItem
                          onClick={onClose}
                          selected={isActive}
                          sx={{
                            py: 1,
                            px: 2,
                            gap: 1,
                            borderRadius: 2,
                            '&.Mui-selected': {
                              backgroundColor: (theme) =>
                                alpha(theme.palette.primary.main, 0.12),
                              '&:hover': {
                                backgroundColor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.18),
                              },
                            },
                          }}
                        >
                          {child.icon}
                          {child.label}
                        </MenuItem>
                      )}
                    </Link>
                  );
                })}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};
